import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface DashboardStats {
  revenue: {
    thisMonth: number;
    lastMonth: number;
    percentageChange: number;
  };
  invoices: {
    total: number;
    paid: number;
    pending: number;
    overdue: number;
  };
  clients: {
    total: number;
    active: number;
    new: number;
  };
  products: {
    total: number;
    lowStock: number;
  };
}

interface RecentInvoice {
  id: string;
  invoice_number: string;
  created_at: string;
  total: number;
  status: string;
  clients: {
    name: string;
  };
}

interface TopClient {
  client_id: string;
  client_name: string;
  total_revenue: number;
  invoice_count: number;
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async (): Promise<DashboardStats> => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

      // Execute ALL queries in parallel using Promise.all
      const [
        thisMonthInvoicesResult,
        lastMonthInvoicesResult,
        allInvoicesResult,
        clientsResult,
        activeClientsResult,
        productsCountResult,
        lowStockResult,
      ] = await Promise.all([
        // This month revenue
        supabase
          .from('invoices')
          .select('total')
          .gte('created_at', startOfMonth.toISOString())
          .neq('status', 'cancelled'),
        
        // Last month revenue
        supabase
          .from('invoices')
          .select('total')
          .gte('created_at', startOfLastMonth.toISOString())
          .lte('created_at', endOfLastMonth.toISOString())
          .neq('status', 'cancelled'),
        
        // All invoices (for stats)
        supabase
          .from('invoices')
          .select('status, due_date')
          .neq('status', 'cancelled'),
        
        // All clients
        supabase
          .from('clients')
          .select('id, created_at', { count: 'exact' }),
        
        // Active clients (this month)
        supabase
          .from('invoices')
          .select('client_id')
          .gte('created_at', startOfMonth.toISOString())
          .neq('status', 'cancelled'),
        
        // Product count
        supabase
          .from('products')
          .select('*', { count: 'exact', head: true }),
        
        // Low stock products
        supabase
          .from('products')
          .select('id, stock, alert_quantity')
          .filter('stock', 'lte', 'alert_quantity'),
      ]);

      // Calculate revenue
      const thisMonthRevenue = thisMonthInvoicesResult.data?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0;
      const lastMonthRevenue = lastMonthInvoicesResult.data?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0;
      const percentageChange = lastMonthRevenue > 0
        ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
        : 0;

      // Calculate invoice stats
      const allInvoices = allInvoicesResult.data || [];
      const invoiceStats = {
        total: allInvoices.length,
        paid: allInvoices.filter(inv => inv.status === 'paid').length,
        pending: allInvoices.filter(inv => inv.status === 'sent' || inv.status === 'draft').length,
        overdue: allInvoices.filter(inv => {
          if (inv.status === 'paid' || !inv.due_date) return false;
          return new Date(inv.due_date) < new Date();
        }).length,
      };

      // Calculate client stats
      const allClients = clientsResult.data || [];
      const uniqueActiveClients = new Set(activeClientsResult.data?.map(inv => inv.client_id)).size;
      const newClients = allClients.filter(
        client => new Date(client.created_at) >= startOfMonth
      ).length;

      return {
        revenue: {
          thisMonth: thisMonthRevenue,
          lastMonth: lastMonthRevenue,
          percentageChange,
        },
        invoices: invoiceStats,
        clients: {
          total: clientsResult.count || 0,
          active: uniqueActiveClients,
          new: newClients,
        },
        products: {
          total: productsCountResult.count || 0,
          lowStock: lowStockResult.data?.length || 0,
        },
      };
    },
  });
}

export function useRecentInvoices(limit: number = 5) {
  return useQuery({
    queryKey: ['dashboard', 'recent-invoices', limit],
    staleTime: 1000 * 60 * 2, // 2 minutes
    queryFn: async (): Promise<RecentInvoice[]> => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          created_at,
          total,
          status,
          clients (
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as RecentInvoice[];
    },
  });
}

export function useTopClients(limit: number = 5) {
  return useQuery({
    queryKey: ['dashboard', 'top-clients', limit],
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async (): Promise<TopClient[]> => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          client_id,
          total,
          clients (
            name
          )
        `)
        .neq('status', 'cancelled');

      if (error) throw error;

      // Group by client and calculate totals
      const clientMap = new Map<string, { name: string; total: number; count: number }>();

      data?.forEach(invoice => {
        const clientId = invoice.client_id;
        const clientName = (invoice.clients as any)?.name || 'Unknown';
        const existing = clientMap.get(clientId);

        if (existing) {
          existing.total += invoice.total || 0;
          existing.count += 1;
        } else {
          clientMap.set(clientId, {
            name: clientName,
            total: invoice.total || 0,
            count: 1,
          });
        }
      });

      // Convert to array and sort by revenue
      const topClients: TopClient[] = Array.from(clientMap.entries())
        .map(([id, data]) => ({
          client_id: id,
          client_name: data.name,
          total_revenue: data.total,
          invoice_count: data.count,
        }))
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, limit);

      return topClients;
    },
  });
}

export function useRevenueChart(months: number = 6) {
  return useQuery({
    queryKey: ['dashboard', 'revenue-chart', months],
    staleTime: 1000 * 60 * 10, // 10 minutes
    queryFn: async () => {
      const now = new Date();
      
      // Build all month queries in parallel
      const monthPromises = [];
      for (let i = months - 1; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

        monthPromises.push(
          supabase
            .from('invoices')
            .select('total')
            .gte('created_at', monthDate.toISOString())
            .lt('created_at', nextMonth.toISOString())
            .neq('status', 'cancelled')
            .then(({ data }) => ({
              month: monthDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
              revenue: data?.reduce((sum, inv) => sum + (inv.total || 0), 0) || 0,
            }))
        );
      }

      // Execute all month queries in parallel
      const chartData = await Promise.all(monthPromises);
      return chartData;
    },
  });
}
